import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getResourcesByIds, updateResource} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {useAppDispatch} from 'src/store/hooks';

import {DEFAULT_RESOURCE, RESOURCE_DESCRIPTION_CONTROL, RESOURCE_DESCRIPTION_REQUIRED_ERROR, RESOURCE_DESCRIPTION_REQUIRED_MESSAGE, RESOURCE_METADATA_CONTROL, RESOURCE_NAME_CONTROL, RESOURCE_NAME_REQUIRED_ERROR, RESOURCE_NAME_REQUIRED_MESSAGE} from '../constants';
import {isValidJsonString, validateMetadata} from '../utils';

import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {ErrorPayload} from 'src/typedef';


type Props = {
    resourceId: string,
};

export const useUpdateResource = ({resourceId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const resourcesResponse = await apiDispatch(getResourcesByIds({requestBody: [resourceId]}));

        if (resourcesResponse.payload.success) {
            if (!resourcesResponse.payload.data[resourceId]) {
                return Promise.resolve({type: '', payload: {
                    success: false,
                    error: true,
                    data: {message: 'Resource not found', status: 404},
                } as ErrorPayload});
            }

            const payload: {success: true, error: false, data: EntitlementResource} = {
                success: true,
                error: false,
                data: resourcesResponse.payload.data[resourceId],
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: resourcesResponse.payload});
        }
    }, [resourceId, apiDispatch]);

    const saveDispatchAction = useCallback(
        (updateResourceRequest: EntitlementResource, signal: AbortSignal) => apiDispatch(
            updateResource({
                resourceId,
                updateResourceRequest: {
                    name: updateResourceRequest.name,
                    description: updateResourceRequest.description,
                    metadata: updateResourceRequest.metadata,
                    types: updateResourceRequest.types,
                },
            }, {signal}),
        ),
        [resourceId, apiDispatch],
    );

    const editValidation = useCallback((data: EntitlementResource) => {
        if (data.name) {
            removeError(RESOURCE_NAME_CONTROL);
        }

        if (data.description) {
            removeError(RESOURCE_DESCRIPTION_CONTROL);
        }

        if (!data.metadata || !data.metadata.trim() || (isValidJsonString(data.metadata))) {
            removeError(RESOURCE_METADATA_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: EntitlementResource) => {
        let valid = true;

        if (!data.name) {
            addError({
                controlId: RESOURCE_NAME_CONTROL,
                code: RESOURCE_NAME_REQUIRED_ERROR,
                message: RESOURCE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: RESOURCE_DESCRIPTION_CONTROL,
                code: RESOURCE_DESCRIPTION_REQUIRED_ERROR,
                message: RESOURCE_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!validateMetadata(data.metadata, addError)) {
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: DEFAULT_RESOURCE,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
